// Finance Agent Hub — unified launcher for the AI agent fleet.
//
// One landing page that:
//   - Shows fleet health, cost, and recent run activity
//   - Exposes a natural-language "Ask AI" strip
//   - Groups all agents as cards for one-click launch
//   - Links to the deep Agent Console for orchestration internals
//
// Designed so a non-technical user can discover and run any agent without
// hunting through the sidebar.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Card, CardContent, CardActions,
    Button, Chip, Stack, TextField, Alert, CircularProgress, Divider,
    IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsightsIcon from '@mui/icons-material/Insights';
import PaymentsIcon from '@mui/icons-material/Payments';
import ShieldIcon from '@mui/icons-material/Shield';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstance from '../../../utils/axiosInstance';
import { nlpFinancialQuery } from './api';

const ACCENT = '#6366f1';
const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// Transaction Intelligence
// Financial Operations
// Autonomous Workflow
// Natural Language Command
// Document Intelligence
// Compliance / Fraud / Risk
// Learning & Continuous Improvement
const AGENT_GROUPS = [
    {
        title: 'Transaction Intelligence',
        description: 'Categorize, reconcile, and audit every transaction as it flows in.',
        color: '#0ea5e9',
        icon: <InsightsIcon />,
        agents: [
            { key: 'categorization', name: 'Categorization Agent', desc: 'Auto-map transactions to GL accounts.', path: '/finance-portal/agents/categorization' },
            { key: 'reconciliation', name: 'Reconciliation Agent', desc: 'Match bank and GL entries continuously.', path: '/finance-portal/agents/reconciliation' },
            { key: 'merchant-rpa', name: 'Merchant RPA Agent', desc: 'Train a robot to pull invoices from merchant portals and reconcile.', path: '/finance-portal/agents/merchant-rpa' },
            { key: 'anomaly', name: 'Anomaly Detection', desc: 'Flag unusual amounts, velocity, or counterparties.', path: '/finance-portal/agents/anomaly-detection' },
            { key: 'data-quality', name: 'Data Quality Agent', desc: 'Find orphan records, stale items, and balance breaks.', path: '/finance-portal/agents/data-quality' },
        ],
    },
    {
        title: 'Financial Operations',
        description: 'Cash flow, budget variance, vendor spend, working capital.',
        color: '#10b981',
        icon: <TrendingUpIcon />,
        agents: [
            { key: 'cashflow', name: 'Cash Flow Forecasting', desc: 'Multi-horizon cash projections with alerts.', path: '/finance-portal/agents/cashflow-forecasting' },
            { key: 'budget', name: 'Budget Variance', desc: 'Real-time budget vs actual with AI root-cause.', path: '/finance-portal/agents/budget-variance' },
            { key: 'vendor-spend', name: 'Vendor Spend Optimizer', desc: 'Concentration, terms, duplicates, benchmarks.', path: '/finance-portal/agents/vendor-spend' },
            { key: 'working-capital', name: 'Working Capital', desc: 'CCC, DSO, DPO, and executive narrative.', path: '/finance-portal/agents/working-capital' },
        ],
    },
    {
        title: 'Autonomous Workflow',
        description: 'Month-end close, AP, AR, and payroll automation.',
        color: '#f59e0b',
        icon: <PaymentsIcon />,
        agents: [
            { key: 'month-end-close', name: 'Month-End Close', desc: 'Accruals, depreciation, revenue recognition DAG.', path: '/finance-portal/agents/month-end-close' },
            { key: 'ap', name: 'AP Automation', desc: 'Zero-touch bills, 3-way match, payment optimizer.', path: '/finance-portal/agents/ap-automation' },
            { key: 'ar', name: 'AR Automation', desc: 'Smart collections, cash application, dunning.', path: '/finance-portal/agents/ar-automation' },
            { key: 'payroll', name: 'Payroll Automation', desc: 'Pre-validation, anomaly, tax verify, reconciliation.', path: '/finance-portal/agents/payroll-automation' },
        ],
    },
    {
        title: 'Natural Language Command',
        description: 'Ask questions, enter transactions, and build reports by chat.',
        color: '#8b5cf6',
        icon: <ChatIcon />,
        agents: [
            { key: 'command-center', name: 'Finance Command Center', desc: 'NL transactions, queries, what-if, reports.', path: '/finance-portal/agents/command-center' },
        ],
    },
    {
        title: 'Document Intelligence',
        description: 'Parse invoices, contracts, and bank statements with vision AI.',
        color: '#ec4899',
        icon: <DescriptionIcon />,
        agents: [
            { key: 'document', name: 'Document Intelligence', desc: 'Invoices, contracts, statements — extracted to structured data.', path: '/finance-portal/agents/document-intelligence' },
        ],
    },
    {
        title: 'Compliance, Fraud & Risk',
        description: 'Fraud detection, SOX controls, regulatory monitoring.',
        color: '#ef4444',
        icon: <ShieldIcon />,
        agents: [
            { key: 'fraud', name: 'Compliance & Fraud Center', desc: 'Ghost vendors, split payments, SOD, filing deadlines.', path: '/finance-portal/agents/fraud-detection' },
        ],
    },
    {
        title: 'Learning & Improvement',
        description: 'Learn from corrections, measure performance, auto-tune.',
        color: '#14b8a6',
        icon: <SchoolIcon />,
        agents: [
            { key: 'feedback-learning', name: 'Feedback Learning', desc: 'User corrections → few-shot examples → knowledge base.', path: '/finance-portal/agents/feedback-learning' },
            { key: 'agent-performance', name: 'Agent Performance', desc: 'Accuracy, override rate, cost-per-op, throughput.', path: '/finance-portal/agents/agent-performance' },
            { key: 'continuous-improvement', name: 'Continuous Improvement', desc: 'Auto-rules, threshold tuning, dead-letter review.', path: '/finance-portal/agents/continuous-improvement' },
        ],
    },
];

const SUGGESTED_QUESTIONS = [
    'What is my current cash position?',
    'Break down AR by aging bucket.',
    'How does Q1 marketing spend compare to last year?',
    'What is our current ratio?',
    'Which customers are likely to pay late this month?',
];

function MetricCard({ label, value, secondary, color = ACCENT, loading }) {
    return (
        <Card sx={{ height: '100%', borderLeft: `3px solid ${color}` }}>
            <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily, fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, mt: 0.5 }}>
                    {loading ? <CircularProgress size={18} /> : (value ?? '–')}
                </Typography>
                {secondary && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                        {secondary}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function fmtMoney(n, digits = 2) {
    if (n == null) return '–';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: digits }).format(Number(n));
}

export default function AgentHub() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cost, setCost] = useState(null);
    const [health, setHealth] = useState(null);
    const [recentRuns, setRecentRuns] = useState([]);
    const [tools, setTools] = useState([]);

    // NL query strip
    const [query, setQuery] = useState('');
    const [queryLoading, setQueryLoading] = useState(false);
    const [queryResult, setQueryResult] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [costRes, healthRes, runsRes, toolsRes] = await Promise.allSettled([
                axiosInstance.get('/api/finance/agent/cost/status'),
                axiosInstance.get('/api/finance/agent/health'),
                axiosInstance.get('/api/finance/agent/runs', { params: { per_page: 5 } }),
                axiosInstance.get('/api/finance/agent/tools'),
            ]);
            if (costRes.status === 'fulfilled') {
                const d = costRes.value.data || {};
                setCost(d.budget || d.data || d);
            }
            if (healthRes.status === 'fulfilled') {
                const d = healthRes.value.data || {};
                setHealth(d.health || d.data || d);
            }
            if (runsRes.status === 'fulfilled') setRecentRuns(runsRes.value.data?.data || []);
            if (toolsRes.status === 'fulfilled') setTools(toolsRes.value.data?.data || []);
        } catch (e) {
            setError(e.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const runQuery = async () => {
        if (!query.trim()) return;
        setQueryLoading(true); setQueryResult(null);
        try {
            const res = await nlpFinancialQuery(query);
            setQueryResult(res?.data?.data || res?.data);
        } catch (e) {
            setQueryResult({ error: e.response?.data?.message || e.message });
        } finally {
            setQueryLoading(false);
        }
    };

    const totalAgents = AGENT_GROUPS.reduce((s, g) => s + g.agents.length, 0);

    // Derived metrics — backend returns budget.daily.usd_cost / budget.monthly.usd_cost
    const costToday = cost?.daily?.usd_cost ?? cost?.today?.usd_cost ?? cost?.daily_usd_cost ?? null;
    const costMonth = cost?.monthly?.usd_cost ?? cost?.month?.usd_cost ?? cost?.monthly_usd_cost ?? null;
    const budgetState = cost?.state || null;
    // Backend returns health.active_instances / crashed_instances
    const fleetActive = health?.active_instances ?? health?.summary?.ready ?? null;
    const fleetCrashed = health?.crashed_instances ?? 0;
    const fleetTotal = (fleetActive ?? 0) + (fleetCrashed ?? 0);
    const runsToday = recentRuns?.filter(r => {
        if (!r.created_at) return false;
        const d = new Date(r.created_at);
        const now = new Date();
        return d.toDateString() === now.toDateString();
    }).length;

    return (
        <Box sx={{ p: 3, fontFamily }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <SmartToyIcon sx={{ color: ACCENT, fontSize: 32 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
                    Finance Agent Hub
                </Typography>
                <Chip
                    label={`${totalAgents} agents`}
                    size="small"
                    sx={{ bgcolor: '#eef2ff', color: ACCENT, fontWeight: 600, fontFamily }}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Open Agent Orchestration Console">
                    <Button
                        size="small"
                        startIcon={<TuneIcon />}
                        onClick={() => navigate('/finance-portal/agent-console')}
                        sx={{ fontFamily, textTransform: 'none' }}
                    >
                        Orchestration Console
                    </Button>
                </Tooltip>
                <Tooltip title="Refresh">
                    <IconButton onClick={loadAll} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily }}>
                Single launcher for every AI agent in your finance stack. Pick an agent, or ask the AI a question below.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Fleet metrics */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        label="Registered Agents"
                        value={totalAgents}
                        secondary={AGENT_GROUPS.length + ' modules'}
                        color="#6366f1"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        label="Registered Tools"
                        value={tools.length || '–'}
                        secondary="Callable by agents"
                        color="#0ea5e9"
                        loading={loading && !tools.length}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        label="Fleet Status"
                        value={fleetTotal > 0 ? `${fleetActive ?? 0} active` : 'Idle'}
                        secondary={fleetCrashed > 0 ? `${fleetCrashed} crashed` : 'All healthy'}
                        color={fleetCrashed > 0 ? '#ef4444' : '#10b981'}
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        label="AI Cost (Today / MTD)"
                        value={fmtMoney(costToday ?? 0, 4)}
                        secondary={'MTD: ' + fmtMoney(costMonth ?? 0, 2) + (budgetState && budgetState !== 'OK' ? ' • ' + budgetState : '')}
                        color={budgetState === 'HARD_STOP' ? '#ef4444' : budgetState === 'WARN' ? '#f59e0b' : '#f59e0b'}
                        loading={loading}
                    />
                </Grid>
            </Grid>

            {/* NL question strip */}
            <Paper sx={{ p: 2, mb: 2, borderLeft: `3px solid ${ACCENT}` }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <ChatIcon sx={{ color: ACCENT, fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily }}>
                        Ask the AI anything about your finances
                    </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g. What was our biggest expense category last quarter?"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runQuery(); } }}
                        disabled={queryLoading}
                    />
                    <Button
                        variant="contained"
                        endIcon={queryLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        onClick={runQuery}
                        disabled={queryLoading || !query.trim()}
                        sx={{ fontFamily, textTransform: 'none', minWidth: 110 }}
                    >
                        {queryLoading ? 'Thinking…' : 'Ask AI'}
                    </Button>
                </Stack>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                    {SUGGESTED_QUESTIONS.map((q) => (
                        <Chip
                            key={q}
                            label={q}
                            size="small"
                            variant="outlined"
                            onClick={() => setQuery(q)}
                            sx={{ fontFamily, mb: 0.5 }}
                        />
                    ))}
                </Stack>
                {queryResult && (
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: '#f8fafc' }}>
                        {queryResult.error ? (
                            <Alert severity="warning" sx={{ py: 0 }}>{queryResult.error}</Alert>
                        ) : (
                            <>
                                {queryResult.ai_response || queryResult.ai_answer || queryResult.answer || queryResult.response ? (
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily }}>
                                        {queryResult.ai_response || queryResult.ai_answer || queryResult.answer || queryResult.response}
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: 11, overflowX: 'auto' }}>
                                        {JSON.stringify(queryResult, null, 2)}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>
                )}
            </Paper>

            {/* Agent groups */}
            <Grid container spacing={2}>
                {AGENT_GROUPS.map((group) => (
                    <Grid item xs={12} md={6} lg={4} key={group.title}>
                        <Paper sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                                       borderLeft: `3px solid ${group.color}` }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ color: group.color, display: 'flex' }}>{group.icon}</Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily, flexGrow: 1 }}>
                                        {group.title}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                    {group.description}
                                </Typography>
                            </Box>
                            <Stack divider={<Divider />} sx={{ flexGrow: 1 }}>
                                {group.agents.map((a) => (
                                    <Box
                                        key={a.key}
                                        sx={{
                                            px: 2, py: 1.25,
                                            cursor: 'pointer',
                                            transition: 'background 120ms',
                                            '&:hover': { bgcolor: '#f1f5f9' },
                                        }}
                                        onClick={() => navigate(a.path)}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily }}>
                                                    {a.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                                    {a.desc}
                                                </Typography>
                                            </Box>
                                            <ArrowForwardIcon sx={{ fontSize: 18, color: group.color }} />
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Recent activity */}
            <Paper sx={{ p: 2, mt: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <BuildIcon sx={{ fontSize: 20, color: ACCENT }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily, flexGrow: 1 }}>
                        Recent Agent Activity
                    </Typography>
                    <Chip size="small" label={`${runsToday} run${runsToday === 1 ? '' : 's'} today`}
                          sx={{ bgcolor: '#eef2ff', color: ACCENT, fontFamily }} />
                </Stack>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {!loading && recentRuns.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily }}>
                        No agent runs yet. Pick an agent above to get started.
                    </Typography>
                )}
                {recentRuns.map((r) => (
                    <Box key={r.id} sx={{ py: 1, borderTop: '1px solid #f1f5f9' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                                size="small"
                                label={r.status}
                                color={
                                    r.status === 'DONE' ? 'success' :
                                    r.status === 'FAILED' ? 'error' :
                                    r.status === 'NEEDS_APPROVAL' ? 'warning' : 'default'
                                }
                            />
                            <Typography variant="body2" sx={{ fontFamily, flexGrow: 1 }}>
                                {(r.task || '').slice(0, 120)}
                            </Typography>
                            {r.confidence != null && (
                                <Chip size="small" variant="outlined"
                                      label={`confidence ${Math.round((Number(r.confidence) || 0) * 100)}%`}
                                      sx={{ fontFamily }} />
                            )}
                            {r.usd_cost != null && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
                                    {fmtMoney(r.usd_cost, 4)}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                ))}
                <Box sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => navigate('/finance-portal/agent-console')}
                            sx={{ fontFamily, textTransform: 'none' }}>
                        View full history in Agent Console →
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
